import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, UserIcon } from "@heroicons/react/outline";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import Table from "../../../../Components/v2/Table";
import TableActions from "../../../../Components/v2/Table/TableActions";
import EmployeeModal from "../../../../Components/v2/Accounting/EmployeeModal";
import {
  addLocalNotification,
  clearBusinessEmployee,
  clearBusinessPartnerError,
  requestDeleteBusinessEmployee,
  requestListBusinessEmployee,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import { cancelAPIRequest } from "../../../../Utils";
import Pagination from "../../../../Components/v2/Pagination";
import { ModalAlert } from "../../../../Components/ModalAlert";
import AllowanceModal from "../../../../Components/v2/Accounting/AllowanceModal";
import DeductionModal from "../../../../Components/v2/Accounting/DeductionModal";
import EmployeeDetailModal from "../../../../Components/v2/Accounting/EmployeeDetailModal";
import { useNavigate } from "react-router-dom";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const Employees = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [allowanceModalOpen, setAllowanceModalOpen] = useState(false);
  const [deductionModalOpen, setDeductionModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();
  const {
    request,
    employees,
    employeesLoading,
    employeesError,
    employeeNextTokens,
    deletedEmployeeId,
    deleteEmployeeLoading,
    deleteEmployeeError,
  } = useBusinessTransactionState();
  const { user } = useAuthState();

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      setPage(1);
      requestListBusinessEmployee(dispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
      });
    }
  }, [dispatch, perPage, user?.mainBusiness?.id]);

  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearBusinessEmployee(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  //  Handle delete success
  useEffect(() => {
    if (deletedEmployeeId) {
      // Close modal
      setShowConfirmModal(false);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-employee-${deletedEmployeeId}`,
        type: "success",
        title: "common.success",
        body: "employees.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedEmployeeId]);

  useEffect(() => {
    if (deleteEmployeeError) {
      setShowConfirmModal(false);
    }
  }, [deleteEmployeeError]);

  useEffect(() => {
    if (deleteEmployeeError && !showConfirmModal) {
      setShowErrorModal(true);
    }
  }, [deleteEmployeeError, showConfirmModal]);

  const loadPage = (selectedPage) => {
    requestListBusinessEmployee(dispatch, {
      page: selectedPage,
      limit: perPage,
      businessID: user?.mainBusiness?.id,
      nextToken: employeeNextTokens[selectedPage],
    });
  };

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (employeeNextTokens[selectedPage] || selectedPage === 1) {
      loadPage(selectedPage);
    }
  };

  // The server lists oldest first, so reload the page rather than slotting the
  // new employee in: it lands where it will be after a refresh.
  const handleCreated = () => {
    loadPage(page);
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  const handleAdd = () => {
    setEmployeeModalOpen(true);
    setSelectedEmployee(null);
  };

  const handleViewDetail = (employee) => {
    setSelectedEmployee(employee);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleEditFromDetail = (employee) => {
    setDetailModalOpen(false);
    setSelectedEmployee(employee);
    setEmployeeModalOpen(true);
  };

  const handleManageAllowanceFromDetail = (employee) => {
    setDetailModalOpen(false);
    setSelectedEmployee(employee);
    setAllowanceModalOpen(true);
  };

  const handleManageDeductionFromDetail = (employee) => {
    setDetailModalOpen(false);
    setSelectedEmployee(employee);
    setDeductionModalOpen(true);
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeModalOpen(true);
  };

  const handleDelete = (employee) => {
    setSelectedEmployee(employee);
    setShowConfirmModal(true);
  };

  const onDelete = () => {
    requestDeleteBusinessEmployee(dispatch, {
      id: selectedEmployee?.id,
    });
  };

  const getDeleteDescription = () => {
    if (!selectedEmployee) return null;
    const name =
      `${selectedEmployee.firstName || ""} ${selectedEmployee.lastName || ""}`.trim();
    const employmentTypeKey =
      selectedEmployee.employmentType === "DAILY_WORKER"
        ? "employees.employmentType.daily"
        : `employees.employmentType.${selectedEmployee.employmentType?.toLowerCase()}`;
    const employmentType = t(employmentTypeKey);
    const position = [selectedEmployee.position, selectedEmployee.department]
      .filter(Boolean)
      .join(" - ");
    const contact = selectedEmployee.email || selectedEmployee.phone || null;
    const parts = [
      selectedEmployee.employeeID
        ? `${name} (${selectedEmployee.employeeID})`
        : name,
      `${t("employees.modal.description.asEmploymentType")} ${employmentType}`,
      position || null,
      contact || null,
    ].filter(Boolean);
    return t("employees.modal.description.deleteItem", {
      info: parts.join(", "),
      interpolation: { escapeValue: false },
    });
  };

  const handleClose = () => {
    cancelAPIRequest(request);
    setEmployeeModalOpen(false);
    setAllowanceModalOpen(false);
    setDeductionModalOpen(false);
    setShowConfirmModal(false);
    setSelectedEmployee(null);
  };

  const handleViewSalaryTable = (employee) => {
    navigate(`/employee/salary/${employee.id}`);
  };

  const handleEmployeeAttendance = (employee) => {
    navigate(`/v2/accounting/employees/attendance/${employee.id}`);
  };

  const handleViewAttendanceFromDetail = (employee) => {
    setDetailModalOpen(false);
    navigate(`/v2/accounting/employees/attendance/${employee.id}`);
  };

  const handleViewSalaryTableFromDetail = (employee) => {
    setDetailModalOpen(false);
    navigate(`/employee/salary/${employee.id}`);
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Page header */}
        <AccountingPageHeader
          title="employees.label"
          primaryAction={{
            label: t("employees.header.title.add"),
            icon: <PlusIcon className="h-5 w-5" />,
            onClick: handleAdd,
          }}
        />

        {/* Employees Table */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <Table
            isLoading={employeesLoading}
            error={employeesError}
            onRetry={() => onPageChange(page)}
            columns={[
              {
                header: "employees.fields.name",
                sortable: true,
                sortKey: "name",
                accessor: (employee) => (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {employee.firstName} {employee.lastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {employee.employeeID}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                header: "employees.fields.position",
                accessor: (employee) => {
                  if (!employee.position && !employee.department) return "-";

                  return (
                    <div>
                      <div className="text-sm text-gray-900">
                        {employee.position || "-"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {employee.department || "-"}
                      </div>
                    </div>
                  );
                },
              },
              {
                header: "employees.fields.employmentType",
                accessor: (employee) => (
                  <div className="text-sm text-gray-900">
                    {employee.employmentType
                      ? t(
                          `employees.employmentType.${
                            employee.employmentType === "DAILY_WORKER"
                              ? "daily"
                              : employee.employmentType.toLowerCase()
                          }`,
                        )
                      : "-"}
                  </div>
                ),
              },
              {
                header: "employees.fields.contact",
                accessor: (employee) => {
                  if (!employee.email && !employee.phone) return "-";

                  return (
                    <div>
                      <div className="text-sm text-gray-900">
                        {employee.email || "-"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {employee.phone || "-"}
                      </div>
                    </div>
                  );
                },
              },
              {
                header: "common.actions.label",
                accessor: (employee, index) => (
                  <TableActions
                    position={index === employees.length - 1 ? "top" : "bottom"}
                    actions={[
                      {
                        label: t("common.view"),
                        onClick: () => handleViewDetail(employee),
                      },
                      {
                        label: t("common.edit"),
                        onClick: () => handleEdit(employee),
                      },
                      {
                        label: t("employees.attendances.label"),
                        onClick: () => handleEmployeeAttendance(employee),
                      },
                      {
                        label: t("employees.salaries.viewSalaryTable"),
                        onClick: () => {
                          handleViewSalaryTable(employee);
                        },
                      },
                      {
                        label: t("employees.allowances.label"),
                        onClick: () => {
                          setAllowanceModalOpen(true);
                          setSelectedEmployee(employee);
                        },
                      },
                      {
                        label: t("employees.deductions.label"),
                        onClick: () => {
                          setDeductionModalOpen(true);
                          setSelectedEmployee(employee);
                        },
                      },
                      {
                        label: t("common.delete"),
                        onClick: () => handleDelete(employee),
                        variant: "danger",
                        disabled: Boolean(employee.inUse),
                        tooltip: employee.inUse
                          ? t("common.inUseInPayroll")
                          : undefined,
                      },
                    ]}
                  />
                ),
                align: "right",
              },
            ]}
            data={employees}
          />
        </div>

        {/* Pagination */}
        <Pagination
          nextTokens={employeeNextTokens}
          currentPage={page}
          isLoading={employeesLoading}
          onPageChange={onPageChange}
          perPage={perPage}
          onPerPageChange={onPerPageChange}
        />

        {/* Employee Detail Modal */}
        <EmployeeDetailModal
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          onEdit={handleEditFromDetail}
          onViewAttendance={handleViewAttendanceFromDetail}
          onViewSalaryTable={handleViewSalaryTableFromDetail}
          onManageAllowance={handleManageAllowanceFromDetail}
          onManageDeduction={handleManageDeductionFromDetail}
          employee={selectedEmployee}
        />

        {/* Employee Modal */}
        {employeeModalOpen && (
          <EmployeeModal
            open={employeeModalOpen}
            onClose={handleClose}
            onCreated={handleCreated}
            employee={selectedEmployee}
          />
        )}

        {/* Allowance Modal */}
        {allowanceModalOpen && (
          <AllowanceModal
            open={allowanceModalOpen}
            onClose={handleClose}
            employee={selectedEmployee}
          />
        )}

        {/* Deduction Modal */}
        {deductionModalOpen && (
          <DeductionModal
            open={deductionModalOpen}
            onClose={handleClose}
            employee={selectedEmployee}
          />
        )}

        {/* Delete Modal */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setShowConfirmModal(open);
          }}
          title="employees.modal.title.delete"
          description="employees.modal.description.delete"
          descriptionNode={getDeleteDescription()}
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          loadingButton={deleteEmployeeLoading}
          disabledButton={deleteEmployeeLoading}
          onConfirm={onDelete}
          onCancel={handleClose}
        />

        {/* Error Modal */}
        {deleteEmployeeError && (
          <ErrorDialog
            open={showErrorModal}
            error={deleteEmployeeError}
            onClose={() => {
              setShowErrorModal(false);
              clearBusinessPartnerError(dispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};
export default Employees;
